import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Alert,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DefaultModal from "../DefaultModal";
import { putCotacaoInterna, putIncluirArquivoCotacao } from "../../Services";
import { formatted, validarCNPJ } from "../../Utils";
import { LoadingButton } from "@mui/lab";

const DEFAULT_FORM = {
  objeto_licitacao: "",
  cnpj: "",
  link: "",
  file: "",
  fileName: "",
  mimeType: "",
  itens: [
    {
      descricao_item: "",
      quantidade_item: "",
      descricao_detalhada_item: "",
      marca: "",
      modelo: "",
      fabricante: "",
      unidade_fornecimento: "",
      valorUnitario: "",
    },
  ],
};

const FormularioCotacao = ({ open, onClose, addNotification }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [exibirAviso, setExibirAviso] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usarLink, setUsarLink] = useState(false);

  

  const handleChange = (e, index) => {
    let { name, value } = e.target;

    if (index !== undefined) {
      // Atualiza um item específico
      if (name === "valorUnitario") {
        value = formatted.moeda(value);
      }

      if (name === "quantidade_item") {
        value = Number(formatted.numero(value));
      }

      const novosItens = [...formData.itens];
      novosItens[index] = { ...novosItens[index], [name]: value };
      setFormData({ ...formData, itens: novosItens });
    } else {
      // Atualiza os dados gerais
      if (name === "cnpj") {
        value = formatted.cnpjCpfMask(value);
      }

      if (name === "link") {
        const temLink = value.trim().length > 0;
        setFormData({
          ...formData,
          link: value,
          ...(temLink
            ? {
                file: "",
                fileName: "",
                mimeType: "",
              }
            : {}),
        });
        return;
      }

      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const maxSize = 80 * 1024 * 1024; // 80 MB

    if (file.size > maxSize) {
      addNotification(
        "Arquivo muito grande. O tamanho máximo permitido é 80 MB.",
        true,
        "info"
      );
      return;
    }

    if (file) {
      setFormData({
        ...formData,
        link: "",
        file: file,
        fileName: file.name,
        mimeType: file.type,
      });
    }
  };

  const handleToggleOrigem = (event) => {
    const checked = event.target.checked;
    setUsarLink(checked);

    if (checked) {
      setFormData((prev) => ({
        ...prev,
        file: "",
        fileName: "",
        mimeType: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      link: "",
    }));
  };

  const handleAddItem = () => {
    // Adiciona um novo item vazio à lista de itens
    setFormData({
      ...formData,
      itens: [
        ...formData.itens,
        {
          descricao_item: "",
          quantidade_item: "",
          descricao_detalhada_item: "",
          marca: "",
          modelo: "",
          fabricante: "",
          unidade_fornecimento: "",
          valorUnitario: "",
        },
      ],
    });
  };

  const handleRemoveItem = (index) => {
    // Remove um item específico da lista
    const novosItens = formData.itens.filter((_, i) => i !== index);
    setFormData({ ...formData, itens: novosItens });
  };

  const showMessage = () => {
    setExibirAviso(true);
    const box = document.getElementById("box-nova-cotacao");
    box.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!exibirAviso) {
      showMessage();
      return;
    }

    const _formData = { ...formData };
    const hasFile = Boolean(formData.file);
    const linkNormalizado = formData.link?.trim() || "";
    const hasLink = Boolean(linkNormalizado);

    // Validação básica
    if (
      !formData.objeto_licitacao ||
      !formData.cnpj ||
      formData.itens.length === 0
    ) {
      addNotification("Preencha todos os campos obrigatórios.", true, "info");
      return;
    }

    if (usarLink && !hasLink) {
      addNotification("Informe um link publico para continuar.", true, "info");
      return;
    }

    if (!usarLink && !hasFile) {
      addNotification("Anexe um arquivo PDF para continuar.", true, "info");
      return;
    }

    if ((hasFile && hasLink) || (!hasFile && !hasLink)) {
      addNotification("Envie apenas um: arquivo PDF ou link publico.", true, "info");
      return;
    }

    if (hasLink) {
      try {
        const parsedUrl = new URL(linkNormalizado);
        const protocoloValido =
          parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";

        if (!protocoloValido || !parsedUrl.hostname) {
          throw new Error("Link invalido");
        }
      } catch (error) {
        addNotification("Informe um link publico valido.", true, "info");
        return;
      }
    }

    if (!validarCNPJ(formData.cnpj)) {
      addNotification("O CNPJ informado não é válido.", true, "info");
      return;
    }

    _formData.cnpj = formatted.numero(_formData.cnpj);

    for (let index = 0; index < _formData.itens.length; index++) {
      let { valorUnitario, quantidade_item } = _formData.itens[index];

      valorUnitario = Number(
        valorUnitario.replace("R$ ", "").replaceAll(".", "").replace(",", ".")
      );

      if (!quantidade_item) {
        addNotification("Prencha a quantidade", true, "info");

        return;
      }

      if (!valorUnitario) {
        addNotification(
          `Prencha o valor unitário do item ${index + 1}`,
          true,
          "info"
        );

        return;
      }

      _formData.itens[index].valorUnitario = valorUnitario;
    }

    try {
      setLoading(true);
      const { file } = formData;
      delete _formData.file;
      _formData.link = hasLink ? linkNormalizado : null;

      const response = await putCotacaoInterna(_formData);

      if (!response.success) {
        return;
      }

      if (hasFile) {
        const subirContrato = await putIncluirArquivoCotacao(response.data, file);

        if (!subirContrato.success) {
          return;
        }
      }

      addNotification("Cotação incluída com sucesso!", true, "success");

      onClose();
    } catch (error) {
      addNotification(
        "Erro ao enviar o formulário. Verifique os dados e tente novamente.",
        true,
        "info"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFormData(DEFAULT_FORM);
    setExibirAviso(false);
    setUsarLink(false);
  }, [open]);

  return (
    <DefaultModal
      style={{ maxHeight: "85vh", overflowY: "hidden" }}
      title={"Nova cotação"}
      open={open}
      onClose={onClose}
    >
      <Box>
        {exibirAviso ? (
          <Alert severity="warning" style={{ marginBottom: 15 }}>
            Atenção! Antes de enviar revise os dados pois os mesmos não poderão
            ser alterados posteriormente, após a revisão clique em enviar
            novamente!
          </Alert>
        ) : null}
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={2}
            id="box-nova-cotacao"
            style={{
              overflowY: "scroll",
              height: "50vh",
              paddingRight: 15,
              marginTop: 20,
              paddingBottom: 10,
            }}
          >
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Objeto da Licitação"
                name="objeto_licitacao"
                value={formData.objeto_licitacao}
                onChange={handleChange}
                size="small"
                multiline
                rows={4}
                required
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CNPJ do Fornecedor"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={usarLink}
                    onChange={handleToggleOrigem}
                    color="primary"
                  />
                }
                label={usarLink ? "Usar link publico" : "Usar arquivo PDF"}
              />
            </Grid>
            {usarLink ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Link publico"
                  name="link"
                  value={formData.link}
                  onChange={handleChange}
                  size="small"
                  placeholder="https://..."
                  helperText="Informe um link publico."
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
                {formData.fileName ? (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    Arquivo selecionado: {formData.fileName}
                  </Typography>
                ) : null}
              </Grid>
            )}

            {/* Itens dinâmicos */}
            {formData.itens.map((item, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      Item {index + 1}
                    </Typography>
                    {formData.itens.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descrição do Item"
                    name="descricao_item"
                    value={item.descricao_item}
                    onChange={(e) => handleChange(e, index)}
                    size="small"
                    required
                    inputProps={{ maxLength: 150 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descrição Detalhada do Item"
                    name="descricao_detalhada_item"
                    value={item.descricao_detalhada_item}
                    onChange={(e) => handleChange(e, index)}
                    multiline
                    rows={4}
                    size="small"
                    required
                    inputProps={{ maxLength: 350 }}
                  />
                </Grid>
                <Grid item xs={6}>

                  <TextField
                    fullWidth
                    label="Marca"
                    name="marca"
                    value={item.marca}
                    onChange={(e) => handleChange(e, index)}
                    size="small"
                    
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Modelo"
                    name="modelo"
                    value={item.modelo}
                    onChange={(e) => handleChange(e, index)}
                    size="small"
                    
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Fabricante"
                    name="fabricante"
                    value={item.fabricante}
                    onChange={(e) => handleChange(e, index)}
                    size="small"                    
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Quantidade"
                    name="quantidade_item"
                    value={item.quantidade_item}
                    onChange={(e) => handleChange(e, index)}
                    size="small"
                    required
                    inputProps={{ maxLength: 7 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Unidade de Fornecimento"
                    name="unidade_fornecimento"
                    value={item.unidade_fornecimento}
                    onChange={(e) => handleChange(e, index)}
                    size="small"
                    required
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Valor Unitário"
                    name="valorUnitario"
                    value={item.valorUnitario}
                    onChange={(e) => handleChange(e, index)}
                    size="small"
                    required
                    inputProps={{ maxLength: 15 }}
                  />
                </Grid>
              </React.Fragment>
            ))}

            {/* Botão para adicionar itens */}
          </Grid>
          <Grid container spacing={2} style={{ paddingTop: 40 }}>
            <Grid item xs={12}>
              <LoadingButton
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                fullWidth
                size="small"
                loading={loading}
              >
                Incluir Item
              </LoadingButton>
            </Grid>

            {/* Botão de envio */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Enviar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </DefaultModal>
  );
};

export default FormularioCotacao;

